import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Loader2, Edit } from 'lucide-react';
import { payrollApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn, formatCurrency } from '../../lib/utils';

interface RunFormData {
  pay_period_start: string;
  pay_period_end: string;
  pay_date: string;
  status: string;
}

const PayrollRuns: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [formData, setFormData] = useState<RunFormData>({
    pay_period_start: '',
    pay_period_end: '',
    pay_date: '',
    status: 'DRAFT',
  });

  const { data: runs = [], isLoading, error } = useQuery({
    queryKey: ['payrollRuns', filterStatus],
    queryFn: async () => {
      const response = await payrollApi.runs.getAll({
        status: filterStatus !== 'all' ? filterStatus : undefined,
      });
      return response as any[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => payrollApi.runs.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payrollRuns'] });
      setIsDialogOpen(false);
      setEditingRun(null);
      resetForm();
      toast({ title: 'Payroll run updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (!formData.pay_period_start || !formData.pay_period_end || !formData.pay_date) {
      toast({ title: 'Error', description: 'Please fill required fields', variant: 'destructive' });
      return;
    }

    if (editingRun) {
      updateMutation.mutate({ id: editingRun.id, data: formData });
    }
  };

  const openDialog = (run: any) => {
    setEditingRun(run);
    setFormData({
      pay_period_start: run.pay_period_start,
      pay_period_end: run.pay_period_end,
      pay_date: run.pay_date,
      status: run.status,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      pay_period_start: '',
      pay_period_end: '',
      pay_date: '',
      status: 'DRAFT',
    });
    setEditingRun(null);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700';
      case 'PROCESSING':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800';
      case 'PENDING_APPROVAL':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
      case 'APPROVED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
      case 'PAID':
        return 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800';
      case 'CANCELLED':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700';
    }
  };

  const getStatusBadge = (status: string) => {
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none capitalize shrink-0", getStatusBadgeClass(status))} variant="outline">
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading payroll runs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 dark:bg-rose-950/15 border border-rose-100 dark:border-rose-900/30 text-rose-600 dark:text-rose-400 p-6 rounded-2xl flex items-center gap-3 animate-in fade-in duration-300">
        <div>
          <p className="font-bold">Error loading payroll runs</p>
          <p className="text-xs font-semibold mt-0.5 opacity-90">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Process and manage payroll runs</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          <Select value={filterStatus} onValueChange={setFilterStatus} >
            <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[160px] rounded-xl shadow-sm">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent className="border-slate-100 dark:border-slate-800">
              <SelectItem value="all" className="text-xs font-bold">All Status</SelectItem>
              <SelectItem value="DRAFT" className="text-xs font-bold">Draft</SelectItem>
              <SelectItem value="PROCESSING" className="text-xs font-bold">Processing</SelectItem>
              <SelectItem value="PENDING_APPROVAL" className="text-xs font-bold">Pending Approval</SelectItem>
              <SelectItem value="APPROVED" className="text-xs font-bold">Approved</SelectItem>
              <SelectItem value="PAID" className="text-xs font-bold">Paid</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => navigate('/payroll/run-new')} 
          >
            <Plus className="h-4 w-4 mr-1.5" />
            New Run
          </Button>
        </div>
      </div>

      {/* Main card & Table */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Payroll Runs</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Pay Period</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Pay Date</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Employees</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Total Gross</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Total Net</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs">
                    No payroll runs found. Create your first payroll run.
                  </TableCell>
                </TableRow>
              ) : (
                runs.map((run: any) => (
                  <TableRow key={run.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="font-bold text-xs text-slate-700 dark:text-slate-350 px-8 py-4">
                      {new Date(run.pay_period_start).toLocaleDateString()} - {new Date(run.pay_period_end).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-500 py-4">
                      {new Date(run.pay_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-xs font-bold text-slate-650 dark:text-slate-350 py-4">
                      {run.employee_count || 0}
                    </TableCell>
                    <TableCell className="text-xs font-bold text-slate-850 dark:text-slate-150 py-4">
                      {formatCurrency(run.total_gross || 0)}
                    </TableCell>
                    <TableCell className="text-xs font-black text-[#143A62] dark:text-blue-450 py-4">
                      {formatCurrency(run.total_net || 0)}
                    </TableCell>
                    <TableCell className="py-4">
                      {getStatusBadge(run.status)}
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDialog(run)}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Edit Run</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              Configure payroll run details
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="pay_period_start" className="text-xs font-bold text-slate-400 tracking-wider">Period Start *</Label>
              <Input
                id="pay_period_start"
                type="date"
                value={formData.pay_period_start}
                onChange={(e) => setFormData({ ...formData, pay_period_start: e.target.value })}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay_period_end" className="text-xs font-bold text-slate-400 tracking-wider">Period End *</Label>
              <Input
                id="pay_period_end"
                type="date"
                value={formData.pay_period_end}
                onChange={(e) => setFormData({ ...formData, pay_period_end: e.target.value })}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay_date" className="text-xs font-bold text-slate-400 tracking-wider">Pay Date *</Label>
              <Input
                id="pay_date"
                type="date"
                value={formData.pay_date}
                onChange={(e) => setFormData({ ...formData, pay_date: e.target.value })}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="status" className="text-xs font-bold text-slate-400 tracking-wider">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11 shadow-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-100 dark:border-slate-800">
                  <SelectItem value="DRAFT" className="text-sm font-bold">Draft</SelectItem>
                  <SelectItem value="PROCESSING" className="text-sm font-bold">Processing</SelectItem>
                  <SelectItem value="PENDING_APPROVAL" className="text-sm font-bold">Pending Approval</SelectItem>
                  <SelectItem value="APPROVED" className="text-sm font-bold">Approved</SelectItem>
                  <SelectItem value="PAID" className="text-sm font-bold">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayrollRuns;